import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { areNodesEqualIgnoreAttrs } from '@atlaskit/editor-common/utils/document';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { Transform } from '@atlaskit/editor-prosemirror/transform';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { AiSuggestionsPlugin, Suggestion } from '../../aiSuggestionsPluginType';
import { isSuggestedEditsDefaultOn } from '../../experiments';
import { isSuggestedRemix } from '../../pm-plugins/remixSuggestion';
import { addNoDiffAnalytics } from './analytics';
import { collectTargetLocalIds } from './collectTargetLocalIds';
import { getAnchorLocalId } from './getAnchorLocalId';
import { getSuggestionDiffStepAttribution } from './getSuggestionAgentAttribution';
import { getSuggestionStepRanges } from './getSuggestionStepRanges';
import { applyToolCalls, getPositionsByLocalId } from './toolCalls/applyToolCalls';

/**
 * Options controlling how the diff is rendered. Kept structurally compatible with the
 * `selectSuggestion` action options so both callers can share this helper.
 */
export type ApplySuggestionDiffOptions = {
	hideDeletedDiffs?: boolean;
	scrollIntoView?: boolean;
};

/**
 * Position of the first node in `doc` matching the suggestion's anchor (or, failing that, any
 * of its tool-call target) localIds. Used as a scroll target when there is no visual diff.
 */
export function findAnchorNodePosition(doc: PMNode, suggestion: Suggestion): number | undefined {
	const anchorLocalId = getAnchorLocalId(suggestion);
	if (anchorLocalId) {
		const anchorPositions = getPositionsByLocalId(doc, anchorLocalId);
		if (anchorPositions) {
			return anchorPositions.from;
		}
	}

	for (const toolCall of suggestion.toolCalls ?? []) {
		const localIds =
			toolCall.name === 'removeNodes' ? toolCall.arguments.localIds : [toolCall.arguments.localId];
		for (const localId of localIds) {
			const positions = getPositionsByLocalId(doc, localId);
			if (positions) {
				return positions.from;
			}
		}
	}
	return undefined;
}

/**
 * Open every `expand` / `nestedExpand` ancestor of `pos` so the card's
 * `anchor-name` spans (emitted at the suggestion target) become layout-
 * visible instead of being hidden under the expand's collapsed content
 * wrapper, where CSS anchor positioning can't resolve them.
 *
 * Delegates to `toggleExpandRange` — it writes `expandedState` (the
 * expand's source of truth) *and* sets meta the expand plugin reads to
 * emit `Decoration.node({ forceExpandOpen })`, which is the outer-deco
 * change that makes PM call `ExpandNodeView.update()`. The range starts
 * at the top-level ancestor so `nodesBetween` definitely visits every
 * wrapping expand.
 *
 * Behind `platform_editor_ai_show_diff_patch_2`, an expand at `pos` opens too, with anything inside it.
 */
export function revealAncestorExpands(
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
	doc: PMNode,
	pos: number,
	tr: Transaction,
): void {
	const $pos = doc.resolve(pos);
	const target = fg('platform_editor_ai_show_diff_patch_2') ? doc.nodeAt(pos) : null;
	const targetIsExpand = target ? isExpandNode(target) : false;

	let hasExpandAncestor = false;
	for (let depth = $pos.depth; depth > 0; depth--) {
		if (isExpandNode($pos.node(depth))) {
			hasExpandAncestor = true;
			break;
		}
	}
	if (!hasExpandAncestor && !targetIsExpand) {
		return;
	}

	const from = $pos.depth === 0 ? pos : $pos.before(1);
	const to = target && targetIsExpand ? pos + target.nodeSize : pos;
	api?.expand?.commands.toggleExpandRange(from, to, true)?.({ tr });
}

const isExpandNode = (node: PMNode): boolean =>
	node.type.name === 'expand' || node.type.name === 'nestedExpand';

/**
 * Renders the diff for a suggestion on the given transaction and returns the
 * position to scroll to when there is no diff (so the card still lands next to
 * its anchor). Shared by the initial select, the post-regeneration re-show, and
 * the undo/redo restore path.
 */
export function applySuggestionDiff({
	api,
	suggestion,
	tr,
	options,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	options?: ApplySuggestionDiffOptions;
	suggestion: Suggestion;
	tr: Transaction;
}): number | undefined {
	let noDiffScrollPos: number | undefined;

	const anchorNodePos = findAnchorNodePosition(tr.doc, suggestion);
	if (fg('platform_editor_ai_show_diff_patch_2')) {
		for (const localId of collectTargetLocalIds(suggestion)) {
			const targetNodePos = getPositionsByLocalId(tr.doc, localId)?.from;

			if (targetNodePos !== undefined) {
				revealAncestorExpands(api, tr.doc, targetNodePos, tr);
			}
		}
	} else if (anchorNodePos !== undefined) {
		revealAncestorExpands(api, tr.doc, anchorNodePos, tr);
	}

	// The plugin `view` scrolls remixes itself; skip here so `scrollToPos` doesn't jump to the source.
	const noDiffTargetPos = isSuggestedRemix(suggestion) ? undefined : anchorNodePos;

	const hasToolCalls = (suggestion.toolCalls?.length ?? 0) > 0;

	if (!hasToolCalls) {
		api?.showDiff?.commands.hideDiff?.({ tr });
		addNoDiffAnalytics(api?.analytics?.actions)(tr, suggestion);
		noDiffScrollPos = noDiffTargetPos;
	} else {
		const isolatedTr = new Transform(tr.doc);
		const appliedTr = applyToolCalls({ tr: isolatedTr, suggestion });

		// Expand any collapsed heading sections the suggestion's edits fall inside, so the
		// diff isn't hidden under a collapsed heading. No-op when block-collapse isn't present.
		for (const { from, to } of getSuggestionStepRanges(appliedTr)) {
			api?.blockCollapse?.commands.expandHeadingsContainingRange(from, to)?.({ tr });
		}

		const stepAttribution = getSuggestionDiffStepAttribution(suggestion);

		if (areNodesEqualIgnoreAttrs(appliedTr.before, appliedTr.doc, ['localId'])) {
			addNoDiffAnalytics(api?.analytics?.actions)(tr, suggestion);
			noDiffScrollPos = noDiffTargetPos;
		}

		api?.showDiff?.commands.showDiff({
			stepsWithAttribution: appliedTr.steps
				.map((step, i) => ({
					step: step.invert(appliedTr.docs[i]),
					stepAttribution,
				}))
				.reverse(),
			originalDoc: appliedTr.doc,
			isInverted: true,
			diffType:
				fg('platform_editor_ai_suggested_edits_smart_diff') && fg('platform_editor_ai_smart_diff')
					? 'smart'
					: 'step',
			hideDeletedDiffs: options?.hideDeletedDiffs ?? isSuggestedEditsDefaultOn(),
			scrollIntoView: options?.scrollIntoView ?? true,
			showIndicators: true,
		})({ tr });
	}

	return noDiffScrollPos;
}
